import { useApolloClient } from '@apollo/client'
import { ReactNode, useEffect, useState } from 'react'
import { isGlobalAudienceEnabled } from '../../../../constants/featureFlags'
import { Survey } from '../../../../data/gql-gen/dashboard/graphql'
import { GET_FIELDWORK } from '../../../../data/gql/questionnaire/queries/fieldwork'
import { SurveyIdProvider } from '../../../../hooks/SurveyIdContext'
import { useMarketsSpecification } from '../../../../hooks/audience/useMarketsSpecification'
import { AudienceContextProvider } from '../../../../modules/Audience/Audience.context'
import { useGetProjectByProjectId } from '../../../../modules/Project/Project.hooks'
import { QuestionnaireContextProvider } from '../../../../modules/Questionnaire/Questionnaire.context'
import { getLanguageLabel } from '../../../../utils/HelperFunctions'
import { fieldworkLanguages } from '../../../../utils/fieldworkLanguages'
import Translations from '../../Survey/Translations'
import {
  getMarkets,
  getMasterSurvey,
  surveysByCreation
} from '../markets.utils'
import { resolveTranslationsSurveyId } from './translationsScope'

const MASTER_LANGUAGE = 'en'

interface Language {
  code: string
  name: string
}

/**
 * The union of every market's languages (master/source English excluded), plus
 * the per-market breakdown behind it. Translations are keyed by language, not
 * country, so two markets sharing a language (Canada + Belgium → French)
 * contribute one French entry — but the breakdown is still needed to know which
 * markets a language covers, and so which questions it has to reach (see
 * marketsForLanguage). Languages come from each market's fieldwork allocations,
 * falling back to its locale.
 */
const useMarketsLanguages = (
  surveyIds: string[]
): { languages: Language[]; languagesBySurveyId: Map<string, string[]> } => {
  const client = useApolloClient()
  const [result, setResult] = useState<{
    languages: Language[]
    languagesBySurveyId: Map<string, string[]>
  }>({ languages: [], languagesBySurveyId: new Map() })
  const key = surveyIds.join(',')

  useEffect(() => {
    const ids = key ? key.split(',') : []
    if (ids.length === 0) {
      setResult({ languages: [], languagesBySurveyId: new Map() })
      return
    }

    let cancelled = false

    Promise.all(
      ids.map(async (surveyId) => {
        try {
          const { data } = await client.query({
            query: GET_FIELDWORK,
            variables: { surveyId },
            context: { clientName: 'questionnaire' }
          })
          return fieldworkLanguages(data.fieldwork)
        } catch {
          // Unknown, not "fields nothing" — see the breakdown below.
          return null
        }
      })
    ).then((perMarket) => {
      if (cancelled) {
        return
      }
      const codes = new Set<string>()
      for (const code of perMarket.flat()) {
        if (code && code !== MASTER_LANGUAGE) {
          codes.add(code)
        }
      }
      // A market whose fieldwork failed to load is indistinguishable from one
      // fielding no languages, and a breakdown missing a market silently
      // narrows that language's scope — questions shown only there would drop
      // out of the editor and read as fully translated. Publish no breakdown at
      // all instead, which filters nothing (visibleInAnyMarket([])).
      const complete = perMarket.every((languages) => languages !== null)
      setResult({
        languages: Array.from(codes).map((code) => ({
          code,
          name: getLanguageLabel(code)
        })),
        languagesBySurveyId: complete
          ? new Map(
              ids.map((surveyId, index) => [surveyId, perMarket[index] ?? []])
            )
          : new Map()
      })
    })

    return () => {
      cancelled = true
    }
  }, [key, client])

  return result
}

/**
 * Translations: the existing per-survey translations editor run over the master
 * questionnaire, with the left-hand language list sourced from every market
 * (translations are per-language, not per-country, so there are no market
 * tabs).
 *
 * The editor is scoped to the BASE market, not the master survey — see
 * resolveTranslationsSurveyId. Questionnaire entries are indifferent to the
 * choice (the backend keys the draft by projectId), but audience translations
 * are stored per market and only propagate to the other markets when written
 * against the base.
 *
 * Used by both the multi-market flow and a tracker wave's flow, which supply
 * their own step chrome. A wave passes its own `markets`, since each wave has
 * its own questionnaire.
 */
export const TranslationsPage = ({
  breadcrumb,
  markets: scopedMarkets
}: {
  breadcrumb: ReactNode
  markets?: Survey[]
}) => {
  const { project } = useGetProjectByProjectId()
  const markets = scopedMarkets
    ? surveysByCreation(scopedMarkets)
    : getMarkets(project)
  const masterSurveyId = (
    scopedMarkets ? markets.at(0) : getMasterSurvey(project)
  )?.surveyId
  const globalAudienceEnabled = isGlobalAudienceEnabled()
  // questionnaireAggV2 is a project-level aggregate, so the master's id is just
  // the handle used to read every market's isBase.
  const { baseSurveyId, loading: baseMarketLoading } = useMarketsSpecification(
    globalAudienceEnabled ? masterSurveyId : undefined
  )
  const scopeSurveyId = resolveTranslationsSurveyId({
    globalAudienceEnabled,
    masterSurveyId,
    baseSurveyId,
    baseMarketLoading
  })
  const { languages, languagesBySurveyId } = useMarketsLanguages(
    markets.map((survey) => survey.surveyId)
  )

  return (
    <div>
      {breadcrumb}
      {scopeSurveyId && (
        <SurveyIdProvider value={scopeSurveyId}>
          <AudienceContextProvider key={scopeSurveyId}>
            <QuestionnaireContextProvider>
              <Translations
                availableLanguages={languages}
                globalAudienceOnly={globalAudienceEnabled}
                languagesBySurveyId={languagesBySurveyId}
              />
            </QuestionnaireContextProvider>
          </AudienceContextProvider>
        </SurveyIdProvider>
      )}
    </div>
  )
}
